import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, useRouteRef, configApiRef } from '@backstage/core-plugin-api';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  makeStyles,
} from '@material-ui/core';
import LinkIcon from '@material-ui/icons/Link';
import EditIcon from '@material-ui/icons/Edit';
import { Alert, ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import {
  isCreatableActivityWorkPath,
  isTerminalWorkPath,
  type CreatableActivityWorkPath,
} from '@internal/spassu-code-contract';
import { spassuCodeApiRef } from '../../api';
import {
  ActivityWorkPath,
  AzureBranch,
  AzureProject,
  AzureRepository,
  AzureWorkItemSummary,
  AzureWorkItemDetail,
  Issue,
  ScmProviderDescriptor,
  SearchWorkItemsFilters,
} from '../../api/types';
import { ProviderSelector } from './ProviderSelector';
import { RepoHierarchyPicker, RepoHierarchyValue } from './RepoHierarchyPicker';
import { IssuePicker } from './IssuePicker';
import { activityConnectRouteRef, activityDetailRouteRef } from '../../routes';
import { useProjectRole } from '../../hooks';
import { normalizeRouteRefPath } from '../../utils/routes';
import { WorkItemPicker, WorkItemPreview } from '../WorkItemPicker';
import type {
  ActivityCreationGateway,
  ActivityCreationJourney,
} from '../../activity-creation/contracts';
import {
  activityCreationContentReducer,
  composeActivityDescription,
  createInitialActivityCreationContent,
  validateActivityTitle,
} from '../../activity-creation/activityCreationReducer';
import { ActivityContentFields } from '../../activity-creation/ActivityContentFields';

export interface CreateActivityDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
  journey?: ActivityCreationJourney;
  gateway?: ActivityCreationGateway;
}

const useStyles = makeStyles(theme => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(4),
    gap: theme.spacing(2),
  },
  modeToggle: {
    marginBottom: theme.spacing(2),
  },
  wiSection: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  wiChip: {
    marginBottom: theme.spacing(1),
  },
  wiPreview: {
    marginTop: theme.spacing(1.5),
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
  },
}));

type DialogMode = 'blank' | 'work-item';

const buildAzureCardContext = (detail: AzureWorkItemDetail) => {
  const parts: string[] = [];
  if (detail.descriptionText) parts.push(detail.descriptionText);
  else if (detail.reproStepsText) parts.push(detail.reproStepsText);
  if (detail.acceptanceCriteriaText) {
    parts.push(`## Critérios de Aceitação\n\n${detail.acceptanceCriteriaText}`);
  }
  return parts.join('\n\n');
};

export const CreateActivityDialog = ({
  open,
  onClose,
  onCreated,
  journey,
  gateway,
}: CreateActivityDialogProps) => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);
  const config = useApi(configApiRef);
  const navigate = useNavigate();
  const activityDetailRoute = useRouteRef(activityDetailRouteRef);
  const activityConnectRoute = useRouteRef(activityConnectRouteRef);
  const {
    isAdmin,
    hasPermission,
    getRolesForProject,
    loading: rbacLoading,
    error: rbacError,
  } = useProjectRole();

  const azureBoardsEnabled =
    config.getOptionalBoolean('spassuCode.features.azureBoards') ?? false;

  const [projects, setProjects] = useState<AzureProject[]>([]);
  const [repositories, setRepositories] = useState<AzureRepository[]>([]);
  const [branches, setBranches] = useState<AzureBranch[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [selectedProject, setSelectedProject] = useState('');
  const [selectedRepo, setSelectedRepo] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [content, dispatchContent] = useReducer(
    activityCreationContentReducer,
    undefined,
    createInitialActivityCreationContent,
  );
  const title = content.title.value;
  const description = content.description.value;
  const importedContext = content.description.lastGenerated;
  // Frontier Web é a via padrão: preserva o clone automático e o fluxo web
  // completo, que é o caminho mais usado na criação pela UI.
  const [selectedWorkPath, setSelectedWorkPath] = useState<
    CreatableActivityWorkPath | ''
  >(journey?.workPath ?? 'frontier-web');
  // D2 — seleção de engine de IA. Default claude-code (alinhado com migration 045).
  const [selectedAiEngine, setSelectedAiEngine] = useState<
    'claude-code' | 'codex'
  >('claude-code');

  const [error, setError] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Multi-provider state (Tarefa 5+7). Azure flow continues unchanged — when
  // the selected provider is non-Azure we render the agnostic
  // RepoHierarchyPicker + IssuePicker pair instead.
  const [scmProviders, setScmProviders] = useState<ScmProviderDescriptor[]>([]);
  const [selectedScmProvider, setSelectedScmProvider] =
    useState<ScmProviderDescriptor | null>(null);
  const [agnosticHierarchy, setAgnosticHierarchy] =
    useState<RepoHierarchyValue>({
      owner: null,
      project: null,
      repository: null,
      branch: null,
    });
  const [agnosticIssue, setAgnosticIssue] = useState<Issue | null>(null);

  // Provider escolhido pelo usuário dirige a UI: Azure mantém o fluxo legado
  // (Work Items + project selector), demais providers usam o fluxo agnóstico
  // (RepoHierarchyPicker + IssuePicker). Sem dependência de feature flag —
  // Azure é um provider entre vários, não obrigatório.
  const isAzureFlow = selectedScmProvider?.id === 'azure-devops';
  const isAgnosticProvider =
    !!selectedScmProvider && selectedScmProvider.id !== 'azure-devops';
  const requireTargetBranch =
    journey?.requireTargetBranch ?? selectedWorkPath === 'control-plane';
  const showAiEngine =
    journey?.showAiEngine ?? selectedWorkPath === 'frontier-web';
  const branchLabel =
    journey?.branchLabel ??
    (selectedWorkPath === 'control-plane'
      ? 'Branch base/destino do PR'
      : 'Branch');

  // Work Item state
  const [mode, setMode] = useState<DialogMode>('work-item');
  const [selectedWorkItem, setSelectedWorkItem] =
    useState<AzureWorkItemDetail | null>(null);

  // Role-based defaults
  const projectRoles = useMemo(
    () => (selectedProject ? getRolesForProject(selectedProject) : []),
    [selectedProject, getRolesForProject],
  );
  const canCreateBlank =
    isAdmin ||
    projectRoles.includes('gestor') ||
    projectRoles.includes('analista');

  const wiInitialFilters = useMemo<Partial<SearchWorkItemsFilters>>(() => {
    if (canCreateBlank) return {};
    // Desenvolvedor: default @me + workable states
    return { assignedToMe: true };
  }, [canCreateBlank]);

  // Load Azure projects — só quando o fluxo Azure está EXPLICITAMENTE
  // selecionado. Antes, a condição era "não-agnóstico", o que incluía o
  // estado inicial `selectedScmProvider === null` (antes do listScmProviders
  // resolver), provocando uma chamada listProjects() especulativa que falhava
  // quando Azure não estava configurado e deixava um banner de erro stale.
  // Em fluxo agnóstico (GitHub etc.) o `RepoHierarchyPicker` resolve
  // owners/repos via `/scm/*`.
  useEffect(() => {
    if (!open) return undefined;
    if (!isAzureFlow) {
      setProjects([]);
      return undefined;
    }
    let cancelled = false;

    const loadProjects = async () => {
      setLoadingProjects(true);
      setError(undefined);
      try {
        const result = await api.listProjects();
        if (!cancelled) {
          // Filter by RBAC: only projects where user can create activities
          // Fallback: show all if RBAC is loading/errored (backwards compat)
          const filtered =
            rbacLoading || rbacError
              ? result
              : result.filter(
                  p => isAdmin || hasPermission(p.name, 'activity.create'),
                );
          setProjects(filtered);
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            `Erro ao carregar projetos: ${
              e instanceof Error ? e.message : String(e)
            }`,
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingProjects(false);
        }
      }
    };

    loadProjects();
    return () => {
      cancelled = true;
    };
  }, [open, api, isAzureFlow, rbacLoading, rbacError, isAdmin, hasPermission]);

  // Load registered SCM providers — used by ProviderSelector. The legacy
  // listProjects() call above stays for the existing Azure-only flow.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    api
      .listScmProviders()
      .then(items => {
        if (cancelled) return;
        setScmProviders(items);
        if (items.length > 0 && !selectedScmProvider) {
          // Preferir Azure DevOps quando registrado — preserva o fluxo legado
          // como default e evita o usuário cair no fluxo agnóstico só porque
          // o backend retornou providers em ordem distinta. Se Azure não
          // estiver registrado, cai no primeiro item.
          const preferred =
            items.find(p => p.id === 'azure-devops') ?? items[0];
          setSelectedScmProvider(preferred);
        }
      })
      .catch(() => {
        // Silent — multi-provider is opt-in. Failures fall back to the
        // legacy Azure-only flow that's already loaded by `listProjects`.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, api]);

  // Load repos
  useEffect(() => {
    if (!selectedProject) {
      setRepositories([]);
      setSelectedRepo('');
      return undefined;
    }
    let cancelled = false;

    const loadRepos = async () => {
      setLoadingRepos(true);
      setSelectedRepo('');
      setRepositories([]);
      try {
        const result = await api.listRepositories(selectedProject);
        if (!cancelled) {
          // Filter repos by RBAC: if user has repo-specific permissions, show only those
          if (!rbacLoading && !rbacError && !isAdmin) {
            const myRoles = await api.getMyRoles();
            const projectAccess = myRoles.data.find(
              p => p.projectKey === selectedProject || p.projectKey === '*',
            );
            if (
              projectAccess &&
              !projectAccess.allRepositories &&
              projectAccess.repositories.length > 0
            ) {
              const allowed = new Set(projectAccess.repositories);
              setRepositories(result.filter(r => allowed.has(r.name)));
            } else {
              setRepositories(result);
            }
          } else {
            setRepositories(result);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            `Erro ao carregar repositórios: ${
              e instanceof Error ? e.message : String(e)
            }`,
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingRepos(false);
        }
      }
    };

    loadRepos();
    return () => {
      cancelled = true;
    };
  }, [selectedProject, api, rbacLoading, rbacError, isAdmin]);

  // Load branches
  useEffect(() => {
    if (!selectedRepo || !selectedProject) {
      setBranches([]);
      setSelectedBranch('');
      return undefined;
    }
    const repoObj = repositories.find(r => r.name === selectedRepo);
    if (!repoObj) {
      setBranches([]);
      setSelectedBranch('');
      return undefined;
    }
    let cancelled = false;

    const loadBranches = async () => {
      setLoadingBranches(true);
      setSelectedBranch('');
      setBranches([]);
      try {
        const result = await api.listBranches(selectedProject, repoObj.id);
        if (!cancelled) {
          setBranches(result);
          // Auto-select default branch
          const defaultBranch = result.find(b => b.isDefault);
          if (defaultBranch) {
            setSelectedBranch(defaultBranch.name);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            `Erro ao carregar branches: ${
              e instanceof Error ? e.message : String(e)
            }`,
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingBranches(false);
        }
      }
    };

    loadBranches();
    return () => {
      cancelled = true;
    };
  }, [selectedRepo, selectedProject, repositories, api]);

  // Reset WI state when project changes
  useEffect(() => {
    setSelectedWorkItem(null);
    dispatchContent({ type: 'card.removed' });
    setMode(azureBoardsEnabled ? 'work-item' : 'blank');
  }, [selectedProject, azureBoardsEnabled]);

  const handleSelectWi = useCallback(
    async (summary: AzureWorkItemSummary) => {
      try {
        const detail = await api.getWorkItem(selectedProject, summary.id);
        setSelectedWorkItem(detail);
        dispatchContent({
          type: 'card.selected',
          card: {
            provider: 'azure-devops',
            externalId: String(detail.id),
            title: detail.title,
            context: buildAzureCardContext(detail),
          },
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        if (msg === 'WORK_ITEM_NOT_FOUND') {
          setError('Work Item não encontrado. Pode ter sido removido.');
        } else if (msg === 'AZURE_PERMISSION_DENIED') {
          setError(
            'Permissão negada ao acessar o Work Item. Verifique o escopo do PAT.',
          );
        } else {
          setError(`Erro ao carregar Work Item: ${msg}`);
        }
      }
    },
    [api, selectedProject],
  );

  const clearWorkItem = useCallback(() => {
    setSelectedWorkItem(null);
    dispatchContent({ type: 'card.removed' });
  }, []);

  const handleModeChange = useCallback((val: DialogMode) => {
    if (val === 'blank') {
      setSelectedWorkItem(null);
      dispatchContent({ type: 'card.removed' });
    }
    setMode(val);
  }, []);

  const resetForm = useCallback(() => {
    setSelectedProject('');
    setSelectedRepo('');
    setSelectedBranch('');
    dispatchContent({ type: 'form.reset' });
    setSelectedWorkPath(journey?.workPath ?? 'frontier-web');
    setError(undefined);
    setFieldErrors({});
    setProjects([]);
    setRepositories([]);
    setBranches([]);
    setSelectedWorkItem(null);
    setMode(azureBoardsEnabled ? 'work-item' : 'blank');
    setAgnosticHierarchy({
      owner: null,
      project: null,
      repository: null,
      branch: null,
    });
    setAgnosticIssue(null);
  }, [azureBoardsEnabled, journey?.workPath]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    resetForm();
    onClose();
  }, [submitting, resetForm, onClose]);

  const handleDialogClose = useCallback(
    (_event: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
        return;
      }
      handleClose();
    },
    [handleClose],
  );

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (isAgnosticProvider) {
      if (!agnosticHierarchy.owner) {
        errors.owner = `Selecione um(a) ${
          selectedScmProvider!.hierarchy.ownerLabel
        }`;
      }
      if (
        selectedScmProvider!.hierarchy.hasProject &&
        !agnosticHierarchy.project
      ) {
        errors.project = `Selecione um(a) ${
          selectedScmProvider!.hierarchy.projectLabel ?? 'Project'
        }`;
      }
      if (!agnosticHierarchy.repository) {
        errors.repo = 'Selecione um repositório';
      }
    } else {
      if (!selectedProject) {
        errors.project = 'Selecione um projeto';
      }
      if (!selectedRepo) {
        errors.repo = 'Selecione um repositório';
      }
    }
    const titleError = validateActivityTitle(title);
    if (titleError) errors.title = titleError;
    if (!isCreatableActivityWorkPath(selectedWorkPath)) {
      errors.workPath = 'Selecione uma via de trabalho';
    }
    const effectiveBranch = isAgnosticProvider
      ? agnosticHierarchy.branch?.name
      : selectedBranch;
    if (requireTargetBranch && !effectiveBranch) {
      errors.branch = 'Selecione a branch base/destino do PR';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [
    isAgnosticProvider,
    agnosticHierarchy,
    selectedScmProvider,
    selectedProject,
    selectedRepo,
    selectedWorkPath,
    selectedBranch,
    requireTargetBranch,
    title,
  ]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;
    setSubmitting(true);
    setError(undefined);
    try {
      const workPath = selectedWorkPath as ActivityWorkPath;
      const composedDescription = composeActivityDescription(
        description,
        importedContext,
      );
      const activity = await (gateway?.create ?? api.createActivity.bind(api))(
        isAgnosticProvider
          ? {
              title: title.trim(),
              description: composedDescription,
              workPath,
              provider: selectedScmProvider!.id,
              repoOwner: agnosticHierarchy.owner!.id,
              repoProject: agnosticHierarchy.project?.name ?? null,
              repoName: agnosticHierarchy.repository!.name,
              branch: agnosticHierarchy.branch?.name || undefined,
              issueExternalId: agnosticIssue?.externalId,
              issueType: agnosticIssue?.type,
              issueTitle: agnosticIssue?.title,
              issueState: agnosticIssue?.state,
              issueUrl: agnosticIssue?.url,
              aiEngine: showAiEngine ? selectedAiEngine : undefined,
            }
          : {
              title: title.trim(),
              description: composedDescription,
              azureProject: selectedProject,
              azureRepository: selectedRepo,
              branch: selectedBranch || undefined,
              workPath,
              azureWorkItemId: selectedWorkItem?.id,
              azureWorkItemType: selectedWorkItem?.type,
              azureWorkItemUrl: selectedWorkItem?.url,
              azureWorkItemTitle: selectedWorkItem?.title,
              azureWorkItemState: selectedWorkItem?.state,
              aiEngine: showAiEngine ? selectedAiEngine : undefined,
            },
      );

      const resolvedWorkPath = activity.workPath ?? workPath;

      onCreated?.();
      resetForm();
      onClose();
      if (journey) {
        journey.onCreated(activity);
      } else {
        navigate(
          normalizeRouteRefPath(
            isTerminalWorkPath(resolvedWorkPath)
              ? activityConnectRoute({ activityId: activity.id })
              : activityDetailRoute({ activityId: activity.id }),
          ),
        );
      }
    } catch (e) {
      setError(
        `Erro ao criar atividade: ${
          e instanceof Error ? e.message : String(e)
        }`,
      );
    } finally {
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- T-24a preserva o conjunto legado sem mudar o comportamento.
  }, [
    validate,
    api,
    title,
    description,
    importedContext,
    selectedProject,
    selectedRepo,
    selectedBranch,
    selectedWorkPath,
    selectedWorkItem,
    showAiEngine,
    gateway,
    journey,
    isAgnosticProvider,
    selectedScmProvider,
    agnosticHierarchy,
    agnosticIssue,
    onCreated,
    resetForm,
    onClose,
    navigate,
    activityConnectRoute,
    activityDetailRoute,
  ]);

  const showWiSection =
    azureBoardsEnabled && selectedProject && mode === 'work-item';
  const dialogMaxWidth = showWiSection ? ('md' as const) : ('sm' as const);

  if (submitting) {
    return (
      <Dialog open={open} maxWidth="sm" fullWidth>
        <DialogContent>
          <div className={classes.loadingContainer}>
            <CircularProgress size={48} />
            <Typography variant="h6">Criando atividade...</Typography>
            <Typography variant="body2" color="textSecondary">
              {isTerminalWorkPath(selectedWorkPath || undefined)
                ? 'Preparando o handoff web -> terminal para a mesma activity.'
                : 'Clonando repositório, isso pode levar alguns segundos.'}
            </Typography>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={dialogMaxWidth}
      fullWidth
    >
      <DialogTitle>Nova Atividade</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" className={classes.field}>
            {error}
          </Alert>
        )}

        {/*
         * ProviderSelector renders silently quando há um único provider
         * registrado; com 2+ providers, surge o dropdown. A escolha aqui dirige
         * todo o dialog: Azure mantém o fluxo legado (project + Work Items),
         * demais providers entram no fluxo agnóstico (RepoHierarchyPicker +
         * IssuePicker). Sem feature flag — registro do provider no backend é
         * a única condição.
         */}
        <ProviderSelector
          providers={scmProviders}
          value={selectedScmProvider?.id ?? null}
          onChange={next => {
            setSelectedScmProvider(next);
            // Erro carregado pelo provider anterior (ex.: listProjects falhando
            // no fluxo Azure) não deve persistir após o usuário trocar de
            // provider — confunde quem está só explorando opções.
            setError(undefined);
          }}
        />

        {/* Agnostic flow (GitHub etc.). The Azure-specific blocks below are
         * skipped — the registry-backed `RepoHierarchyPicker` + `IssuePicker`
         * cover the same surface using provider-aware endpoints. */}
        {isAgnosticProvider && selectedScmProvider && (
          <>
            <RepoHierarchyPicker
              provider={selectedScmProvider}
              value={agnosticHierarchy}
              onChange={next => {
                const repositoryChanged =
                  next.repository?.externalId !==
                  agnosticHierarchy.repository?.externalId;
                setAgnosticHierarchy(next);
                if (repositoryChanged) {
                  setAgnosticIssue(null);
                  dispatchContent({ type: 'card.removed' });
                }
                setFieldErrors(prev => ({
                  ...prev,
                  owner: '',
                  project: '',
                  repo: '',
                }));
              }}
            />
            {fieldErrors.owner && (
              <FormHelperText error>{fieldErrors.owner}</FormHelperText>
            )}
            {fieldErrors.project && (
              <FormHelperText error>{fieldErrors.project}</FormHelperText>
            )}
            {fieldErrors.repo && (
              <FormHelperText error>{fieldErrors.repo}</FormHelperText>
            )}
            {fieldErrors.branch && (
              <FormHelperText error>{fieldErrors.branch}</FormHelperText>
            )}

            {agnosticHierarchy.repository && (
              <div className={classes.wiSection}>
                <IssuePicker
                  provider={selectedScmProvider.id}
                  scope={{
                    owner: agnosticHierarchy.owner!.id,
                    project: agnosticHierarchy.project?.name,
                    repo: agnosticHierarchy.repository.name,
                  }}
                  selected={agnosticIssue}
                  onSelect={issue => {
                    setAgnosticIssue(issue);
                    if (!issue) {
                      dispatchContent({ type: 'card.removed' });
                      return;
                    }
                    dispatchContent({
                      type: 'card.selected',
                      card: {
                        provider: selectedScmProvider.id,
                        externalId: issue.externalId,
                        title: issue.title,
                        context: issue.description ?? '',
                      },
                    });
                  }}
                />
              </div>
            )}
          </>
        )}

        {/* Project selector */}
        {!isAgnosticProvider && (
          <FormControl
            variant="outlined"
            size="small"
            fullWidth
            className={classes.field}
            error={!!fieldErrors.project}
          >
            <InputLabel id="create-project-label">
              Projeto Azure DevOps
            </InputLabel>
            <Select
              labelId="create-project-label"
              label="Projeto Azure DevOps"
              value={selectedProject}
              onChange={e => {
                setSelectedProject(e.target.value as string);
                setFieldErrors(prev => ({ ...prev, project: '' }));
              }}
              disabled={loadingProjects}
            >
              {loadingProjects && (
                <MenuItem value="" disabled>
                  Carregando projetos...
                </MenuItem>
              )}
              {!loadingProjects &&
                projects.map(p => (
                  <MenuItem key={p.name} value={p.name}>
                    {p.name}
                  </MenuItem>
                ))}
            </Select>
            {fieldErrors.project && (
              <FormHelperText>{fieldErrors.project}</FormHelperText>
            )}
          </FormControl>
        )}

        {/* Mode toggle (only if feature flag ON + project selected + canCreateBlank) */}
        {!isAgnosticProvider &&
          azureBoardsEnabled &&
          selectedProject &&
          canCreateBlank && (
            <div className={classes.modeToggle}>
              <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={(_e: unknown, val: string | null) => {
                  if (val) handleModeChange(val as DialogMode);
                }}
                size="small"
              >
                <ToggleButton value="work-item">
                  <LinkIcon fontSize="small" style={{ marginRight: 4 }} />
                  Vincular Card do Board
                </ToggleButton>
                <ToggleButton value="blank">
                  <EditIcon fontSize="small" style={{ marginRight: 4 }} />
                  Nova Atividade em Branco
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
          )}

        {/* Work Item section */}
        {!isAgnosticProvider && showWiSection && (
          <div className={classes.wiSection}>
            {selectedWorkItem && (
              <Chip
                icon={<LinkIcon />}
                label={`#${selectedWorkItem.id} — ${selectedWorkItem.type}: ${selectedWorkItem.title}`}
                onDelete={clearWorkItem}
                color="secondary"
                className={classes.wiChip}
              />
            )}

            <WorkItemPicker
              projectId={selectedProject}
              onSelect={handleSelectWi}
              selectedId={selectedWorkItem?.id}
              initialFilters={wiInitialFilters}
              lockAssignedToMe={!canCreateBlank}
              renderEmpty={filters => (
                <div className={classes.emptyState}>
                  {filters.assignedToMe ? (
                    <>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        gutterBottom
                      >
                        Nenhum Work Item atribuído a você neste projeto.
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Peça ao seu Analista de Requisitos ou PO para atribuir
                        um item do backlog.
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Nenhum Work Item encontrado. Refine os filtros.
                    </Typography>
                  )}
                </div>
              )}
            />
            {selectedWorkItem && (
              <div className={classes.wiPreview}>
                <WorkItemPreview
                  projectId={selectedProject}
                  workItemId={selectedWorkItem.id}
                />
              </div>
            )}

            <Divider style={{ margin: '16px 0' }} />
          </div>
        )}

        {/* Repo selector */}
        {!isAgnosticProvider && (
          <FormControl
            variant="outlined"
            size="small"
            fullWidth
            className={classes.field}
            error={!!fieldErrors.repo}
          >
            <InputLabel id="create-repo-label">Repositório</InputLabel>
            <Select
              labelId="create-repo-label"
              label="Repositório"
              value={selectedRepo}
              onChange={e => {
                setSelectedRepo(e.target.value as string);
                setFieldErrors(prev => ({ ...prev, repo: '' }));
              }}
              disabled={!selectedProject || loadingRepos}
            >
              {loadingRepos && (
                <MenuItem value="" disabled>
                  Carregando repositórios...
                </MenuItem>
              )}
              {!loadingRepos &&
                repositories.map(r => (
                  <MenuItem key={r.id} value={r.name}>
                    {r.name}
                  </MenuItem>
                ))}
            </Select>
            {fieldErrors.repo && (
              <FormHelperText>{fieldErrors.repo}</FormHelperText>
            )}
          </FormControl>
        )}

        {/* Branch selector */}
        {!isAgnosticProvider && (
          <FormControl
            variant="outlined"
            size="small"
            fullWidth
            className={classes.field}
          >
            <InputLabel id="create-branch-label">{branchLabel}</InputLabel>
            <Select
              labelId="create-branch-label"
              label={branchLabel}
              value={selectedBranch}
              onChange={e => {
                setSelectedBranch(e.target.value as string);
                setFieldErrors(prev => ({ ...prev, branch: '' }));
              }}
              disabled={!selectedRepo || loadingBranches}
            >
              {loadingBranches && (
                <MenuItem value="" disabled>
                  Carregando branches...
                </MenuItem>
              )}
              {!loadingBranches &&
                branches.map(b => (
                  <MenuItem key={b.name} value={b.name}>
                    {b.name}
                    {b.isDefault ? ' (padrão)' : ''}
                  </MenuItem>
                ))}
            </Select>
            {fieldErrors.branch && (
              <FormHelperText error>{fieldErrors.branch}</FormHelperText>
            )}
          </FormControl>
        )}

        {!journey && (
          <FormControl
            variant="outlined"
            size="small"
            fullWidth
            className={classes.field}
            error={!!fieldErrors.workPath}
          >
            <InputLabel id="create-work-path-label">Via de Trabalho</InputLabel>
            <Select
              labelId="create-work-path-label"
              label="Via de Trabalho"
              value={selectedWorkPath}
              onChange={e => {
                setSelectedWorkPath(
                  e.target.value as CreatableActivityWorkPath,
                );
                setFieldErrors(prev => ({ ...prev, workPath: '' }));
              }}
            >
              <MenuItem value="frontier-web">Frontier Web</MenuItem>
              <MenuItem value="control-plane">Control Plane</MenuItem>
            </Select>
            <FormHelperText>
              {fieldErrors.workPath ||
                (isTerminalWorkPath(selectedWorkPath || undefined)
                  ? 'Fluxos de terminal nao disparam clone automatico na criacao.'
                  : 'Frontier Web preserva o clone automatico e o fluxo atual.')}
            </FormHelperText>
          </FormControl>
        )}

        {/* AI Engine (D2) — escolha o motor que executa as fases. Codex
            tem suporte limitado: fases skill-dependentes ficam bloqueadas
            quando engine=codex (D0.4). */}
        {showAiEngine && (
          <FormControl
            variant="outlined"
            size="small"
            fullWidth
            className={classes.field}
          >
            <InputLabel id="create-ai-engine-label">Motor de IA</InputLabel>
            <Select
              labelId="create-ai-engine-label"
              label="Motor de IA"
              value={selectedAiEngine}
              onChange={e =>
                setSelectedAiEngine(e.target.value as 'claude-code' | 'codex')
              }
              data-testid="create-ai-engine-select"
            >
              <MenuItem value="claude-code">Claude Code (recomendado)</MenuItem>
              <MenuItem value="codex">Codex (OpenAI) — beta</MenuItem>
            </Select>
            <FormHelperText>
              {selectedAiEngine === 'codex'
                ? 'Codex: fases "Dividindo Tasks", "Executando" e "Pronto" ficam bloqueadas no v1 (sem skills).'
                : 'Claude Code: pipeline completo suportado.'}
            </FormHelperText>
          </FormControl>
        )}

        <ActivityContentFields
          title={title}
          description={description}
          descriptionVisible={Boolean(
            mode === 'blank' ||
              selectedWorkItem ||
              isAgnosticProvider ||
              content.description.dirty,
          )}
          importedContext={importedContext}
          card={content.card}
          titleError={fieldErrors.title}
          onTitleChange={value => {
            dispatchContent({ type: 'title.edited', value });
            setFieldErrors(prev => ({ ...prev, title: '' }));
          }}
          onDescriptionChange={value =>
            dispatchContent({ type: 'description.edited', value })
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loadingProjects || loadingRepos || loadingBranches}
        >
          Criar Atividade
        </Button>
      </DialogActions>
    </Dialog>
  );
};
